/**
 * Shared state for Review & Organize All Debts (dt-1) hub + subsection screens.
 */

export const REVIEW_ORGANIZE_HUB_FRAME_ID = 'sframe-199';

export const HUB_SECTION_KEYS = ['s1', 's2', 's3', 's4', 's5'] as const;
export type HubSectionKey = (typeof HUB_SECTION_KEYS)[number];

export function defaultHubSections(): Record<HubSectionKey, boolean> {
  return { s1: false, s2: false, s3: false, s4: false, s5: false };
}

export function mergeHubSections(raw: Record<string, unknown> | undefined): Record<HubSectionKey, boolean> {
  const base = defaultHubSections();
  const hub = raw?.hubSections as Partial<Record<HubSectionKey, boolean>> | undefined;
  if (!hub || typeof hub !== 'object') return base;
  for (const k of HUB_SECTION_KEYS) {
    if (typeof hub[k] === 'boolean') base[k] = hub[k];
  }
  return base;
}

export function countHubSectionsDone(sections: Record<HubSectionKey, boolean>): number {
  return HUB_SECTION_KEYS.filter((k) => sections[k]).length;
}

export async function saveHubSectionsAndSyncDt1(
  loadFrameData: (frameId: string) => Promise<{ data?: Record<string, unknown> } | null>,
  saveFrameData: (frameId: string, data: Record<string, unknown>) => Promise<boolean>,
  updateStepProgress: (
    stepId: string,
    phase: string,
    updates: { status: 'not-started' | 'in-progress' | 'complete' }
  ) => Promise<boolean>,
  nextSections: Record<HubSectionKey, boolean>
): Promise<boolean> {
  const existing = await loadFrameData(REVIEW_ORGANIZE_HUB_FRAME_ID);
  const mergedData: Record<string, unknown> = { ...(existing?.data || {}) };
  mergedData.hubSections = nextSections;
  const ok = await saveFrameData(REVIEW_ORGANIZE_HUB_FRAME_ID, mergedData);
  const doneCount = countHubSectionsDone(nextSections);
  await updateStepProgress('dt-1', 'debts-taxes', {
    status:
      doneCount >= HUB_SECTION_KEYS.length ? 'complete' : doneCount > 0 ? 'in-progress' : 'not-started',
  });
  return ok;
}

export async function syncDt1StepStatusFromHub(
  loadFrameData: (frameId: string) => Promise<{ data?: Record<string, unknown> } | null>,
  updateStepProgress: (
    stepId: string,
    phase: string,
    updates: { status: 'not-started' | 'in-progress' | 'complete' }
  ) => Promise<boolean>
): Promise<void> {
  const hub = await loadFrameData(REVIEW_ORGANIZE_HUB_FRAME_ID);
  const sections = mergeHubSections(hub?.data as Record<string, unknown> | undefined);
  const doneCount = countHubSectionsDone(sections);
  await updateStepProgress('dt-1', 'debts-taxes', {
    status:
      doneCount >= HUB_SECTION_KEYS.length ? 'complete' : doneCount > 0 ? 'in-progress' : 'not-started',
  });
}
